import React, { useState } from 'react';
import { Download, FileText, FileSpreadsheet, Loader2 } from 'lucide-react';
import { Button } from '../../../../components/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../../components/ui/dropdown-menu';
import { exportToCsv } from '../utils/exportToCsv';
import { exportToPdf } from '../utils/exportToPdf';
import { AnalyticsSummary, QRCodeData } from '../types';
import toast from 'react-hot-toast';

interface ExportDropdownProps {
  qrCode: QRCodeData;
  analytics: AnalyticsSummary | undefined;
}

export function ExportDropdown({ qrCode, analytics }: ExportDropdownProps) {
  const [isExporting, setIsExporting] = useState(false);

  const handleExportCsv = () => {
    try {
      exportToCsv({ qrCode, analytics });
      toast.success('CSV exported successfully!');
    } catch (error) {
      console.error('CSV export failed:', error);
      toast.error('Failed to export CSV');
    }
  };

  const handleExportPdf = async () => {
    setIsExporting(true);
    try {
      await exportToPdf({ qrCode, analytics });
      toast.success('PDF report generated!');
    } catch (error) {
      console.error('PDF export failed:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setIsExporting(false);
    }
  };

  const hasData = analytics && analytics.total_scans > 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="secondary"
          size="sm"
          disabled={!hasData || isExporting}
          className="group hover:bg-gray-100 transition-colors"
        >
          {isExporting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Download className="w-4 h-4 transition-transform" />
          )}
          <span className="hidden sm:inline">Export Data</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 p-2 bg-white rounded-2xl border-[--clinqr-border]/10 shadow-2xl">
        <DropdownMenuItem onClick={handleExportCsv} className="cursor-pointer rounded-xl p-3 focus:bg-[--clinqr-primary] focus:text-[--clinqr-bg] transition-all group">
          <FileSpreadsheet className="w-4 h-4 mr-2 text-[--clinqr-primary] group-focus:text-[--clinqr-bg]" />
          <span className="font-medium">Export as CSV</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleExportPdf} className="cursor-pointer rounded-xl p-3 focus:bg-[--clinqr-primary] focus:text-[--clinqr-bg] transition-all group" disabled={isExporting}>
          <FileText className="w-4 h-4 mr-2 text-[--clinqr-primary] group-focus:text-[--clinqr-bg]" />
          <span className="font-medium">Export as PDF Report</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
